import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { SavedViews } from "@/components/SavedViews";
import { Plus, ChevronLeft, ChevronRight, AlertTriangle, ExternalLink } from "lucide-react";

export default function AdminJobs() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const limit = 25;

  useEffect(() => {
    api.get(`/admin/jobs-table?q=${encodeURIComponent(q)}&status=${status}&skip=${skip}&limit=${limit}`)
      .then(({ data }) => setData(data)).catch(() => {});
  }, [q, status, skip]);
  useEffect(() => { setSkip(0); }, [q, status]);

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-jobs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-widest text-volt">ADMIN HQ · RECORDS</div>
          <h1 className="heading text-4xl mt-1">JOBS</h1>
        </div>
        <Link to="/hq/jobs/new" data-testid="hq-create-job"
          className="inline-flex items-center gap-2 bg-blaze text-white heading text-xs tracking-widest px-4 py-2.5 hover:glow-blaze">
          <Plus size={14} /> CREATE JOB
        </Link>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <input data-testid="jobs-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, program, location…"
          className="bg-black border border-neutral-800 px-3 py-2 text-sm w-72 focus:border-volt outline-none" />
        <select data-testid="jobs-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none">
          <option value="">All statuses</option>
          {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <SavedViews page="jobs" params={{ q, status }} onApply={(p) => { setQ(p.q || ""); setStatus(p.status || ""); }} />
        {data && <span className="text-xs text-neutral-500 ml-auto" data-testid="jobs-total">{data.total.toLocaleString()} jobs · showing {skip + 1}–{Math.min(skip + limit, data.total)}</span>}
      </div>

      {!data ? (
        <div className="mt-6 text-neutral-500 text-sm">Loading…</div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto border border-neutral-800">
            <table className="w-full text-sm" data-testid="jobs-table">
              <thead>
                <tr className="text-left text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800 bg-appcard">
                  <th className="py-2.5 px-3">JOB</th>
                  <th className="py-2.5 px-3 hidden md:table-cell">LEVEL</th>
                  <th className="py-2.5 px-3 hidden lg:table-cell">SOURCE</th>
                  <th className="py-2.5 px-3">STATUS</th>
                  <th className="py-2.5 px-3 hidden md:table-cell">CREATED</th>
                  <th className="py-2.5 px-3 text-right">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((j) => (
                  <tr key={j.id} className="border-b border-neutral-900 hover:bg-neutral-900/40" data-testid={`job-row-${j.id}`}>
                    <td className="py-2.5 px-3">
                      <div className="text-white text-xs font-medium flex items-center gap-1.5 flex-wrap">
                        {j.title || `${j.role} — ${j.program_name}`}
                        {j.duplicate_warning && (
                          <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-amber-600 text-amber-400 inline-flex items-center gap-1" data-testid={`job-dup-${j.id}`}>
                            <AlertTriangle size={9} /> POSSIBLE DUPLICATE
                          </span>
                        )}
                        {j.needs_contact && (
                          <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-700 text-red-400">NEEDS CONTACT INFO</span>
                        )}
                      </div>
                      <div className="text-[10px] text-neutral-500 mt-0.5">{j.program_name} {j.location ? `· ${j.location}` : ""} {j.gender ? `· ${j.gender}` : ""}</div>
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell text-neutral-400 text-xs">{j.division || j.level || "—"}</td>
                    <td className="py-2.5 px-3 hidden lg:table-cell text-neutral-500 text-[10px]">{j.source || "manual"}</td>
                    <td className="py-2.5 px-3"><StatusBadge s={j.status} /></td>
                    <td className="py-2.5 px-3 hidden md:table-cell text-neutral-500 text-xs">{(j.created_at || "").slice(0, 10)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="inline-flex gap-2">
                        <Link to={`/jobs/${j.id}`} className="text-neutral-400 hover:text-volt" title="View" data-testid={`job-view-${j.id}`}><ExternalLink size={13} /></Link>
                        <Link to={`/hq/jobs/${j.id}/edit`} className="text-[10px] tracking-widest text-volt hover:underline" data-testid={`job-edit-${j.id}`}>EDIT</Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-neutral-500">No jobs match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-500">
            <button data-testid="jobs-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronLeft size={12} /></button>
            <span>{skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
            <button data-testid="jobs-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronRight size={12} /></button>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ s }) {
  const cfg = {
    open: "border-emerald-500/60 text-emerald-400",
    filled: "border-volt/60 text-volt",
    closed: "border-neutral-600 text-neutral-400",
    pending: "border-amber-500/60 text-amber-400",
  }[s] || "border-neutral-700 text-neutral-400";
  return <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border whitespace-nowrap ${cfg}`}>{(s || "—").toUpperCase()}</span>;
}
